import { useRouter } from 'next/router';

import { Category, ProductPreview } from 'domain/types';
import { CategoriesRepo, ProductPreviewsRepo } from 'domain/repositories';
import Layout from 'components/layout/Layout';
import CategoryNavigation from 'components/shop/CategoryNavigation/CategoryNavigation';
import ProductTiles from 'components/shop/ProductTiles/ProductTiles';
import { flatProductPreviewsWithColors } from 'utils/helpers/products/flatProductPreviewsWithColors';
import { REVALIDATES } from 'domain/constants';
import PageLoader from 'components/ui/loaders/PageLoader';

interface CategoryPageProps {
  categories: Array<Category>;
  products: Array<ProductPreview>;
  category: Category;
}

const CategoryPage: React.FC<CategoryPageProps> = ({
  categories,
  products,
  category
}): JSX.Element => {
  const router = useRouter();

  if (router.isFallback) return <PageLoader />;

  return (
    <Layout
      pageMeta={{
        title: category.metaTitle || category.name,
        description: category.metaDescription || category.description,
        imageUrl: category.image.url
      }}
    >
      <CategoryNavigation categories={categories} categorySlug={category.slug} />

      <ProductTiles products={products} />
    </Layout>
  );
};

export const getStaticPaths = async () => {
  const slugs = await CategoriesRepo.getSlugs();

  const paths = slugs.map(({ slug }) => ({ params: { category: slug } }));

  return {
    paths,
    fallback: true
  };
};

interface CategoryPageContext {
  params: { category: string };
}

export const getStaticProps = async (ctx: CategoryPageContext) => {
  try {
    const {
      params: { category: categorySlug }
    } = ctx;

    const [categories, productsWithColorCollection] = await Promise.all([
      CategoriesRepo.get(),
      ProductPreviewsRepo.getProductsByCategorySlug(categorySlug)
    ]);

    const products = flatProductPreviewsWithColors(productsWithColorCollection);

    const category = categories.find(({ slug }) => slug === categorySlug);

    if (!category)
      return {
        notFound: true,
        revalidate: REVALIDATES.category
      };

    return {
      props: {
        categories,
        products,
        category
      },
      revalidate: REVALIDATES.category
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);

    return {
      notFound: true,
      revalidate: REVALIDATES.category
    };
  }
};

export default CategoryPage;
